import { Button, Grid, Section, Stack, Typography } from '@cmg/design-system';
import defaultTo from 'lodash/defaultTo';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import routeFactory from '../../../../../../common/util/routeFactory';
import { DescriptionList } from '../../../../../../design-system/body-sections/description-list/DescriptionList';
import { DescriptionListItem } from '../../../../../../design-system/body-sections/description-list/DescriptionListItem';
import { sellSideJobFunctionLabels } from '../../../../../../types/domain/user/constants';
import { selectCrmIntegration } from '../../../../shared/ducks';
import type { SalesCoverageUnderwriterEmployeeDetailFragment } from '../graphql/__generated__';
import { SECTIONS_SIZING } from '../models/UserDetailsSection.model';

export type UserAccountSectionProps = Readonly<{
  employee: SalesCoverageUnderwriterEmployeeDetailFragment;
}>;

export function UserAccountSection({ employee }: UserAccountSectionProps) {
  const crmIntegration = useSelector(selectCrmIntegration);
  const navigate = useNavigate();

  const handleCreateUserAccount = useCallback(() => {
    navigate(
      routeFactory.accountUserNew.getUrlPath({ accountSubdomain: crmIntegration?.subdomain ?? '' })
    );
  }, [navigate, crmIntegration]);

  const handleViewUserAccount = useCallback(() => {
    navigate(
      routeFactory.accountUsers.getUrlPath({ accountSubdomain: crmIntegration?.subdomain ?? '' })
    );
  }, [navigate, crmIntegration]);

  const actions = useMemo(() => {
    if (!employee.userAccount) {
      return;
    }
    return [
      <Button key="view-user-account" onClick={handleViewUserAccount}>
        View User Account
      </Button>,
    ];
  }, [employee.userAccount, handleViewUserAccount]);

  return (
    <Grid item {...SECTIONS_SIZING}>
      <Section title="User Account" actions={actions}>
        {employee.userAccount ? (
          <DescriptionList>
            <DescriptionListItem label="Name" value={defaultTo(employee.userAccount?.name, '-')} />
            <DescriptionListItem
              label="Email"
              value={defaultTo(employee.userAccount?.email, '-')}
            />
            <DescriptionListItem
              label="Job Function"
              value={defaultTo(
                sellSideJobFunctionLabels[employee.userAccount?.jobFunction ?? ''],
                '-'
              )}
            />
          </DescriptionList>
        ) : (
          <Stack gap={1}>
            <Typography>No user account found matching this employee key</Typography>
            <Button size="medium" sx={{ width: 151 }} onClick={handleCreateUserAccount}>
              Create User Account
            </Button>
          </Stack>
        )}
      </Section>
    </Grid>
  );
}
